// @givmo/design-system — Textarea (SaaS layer multi-line input).
//
// The multi-line sibling of `Field` for the rebuilt admin/super-admin portals — replaces the
// hand-rolled `<textarea>` one-offs (charity Mission/Notes, ETF/celebrity blurbs, legal/onboarding
// notes). Same SaaS tokens as `Field`: 8px radius, `border-strong` idle border, `shadow-ring` brand
// focus, body text at s14, `text-placeholder` hint, label above (s13 / w500 / text-secondary) and an
// inline helper/error below wired through `aria-describedby`.
import { forwardRef, useId } from 'react';
import type { ReactNode, TextareaHTMLAttributes } from 'react';
import { cn } from '../lib/utils';
import { testIdProp, type TestIdProps } from '../lib/testid';

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement>, TestIdProps {
  /** Label above the textarea. */
  label?: ReactNode;
  /** Helper text below the textarea (hidden when an error message is shown). */
  helper?: ReactNode;
  /** Error state — red border + (with `errorMessage`) an inline message. */
  error?: boolean;
  errorMessage?: string;
  /** Wrapper className (the field column). */
  containerClassName?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  {
    label,
    helper,
    error = false,
    errorMessage,
    className,
    containerClassName,
    id,
    rows = 3,
    testId,
    ...props
  },
  ref,
) {
  const reactId = useId();
  const textareaId = id ?? reactId;
  const errorId = `${textareaId}-error`;
  const helperId = `${textareaId}-helper`;
  const hasErrorMessage = error && !!errorMessage;
  const describedBy = hasErrorMessage ? errorId : helper != null ? helperId : undefined;

  return (
    <div className={cn('flex flex-col items-stretch', containerClassName)}>
      {label != null && (
        <label
          htmlFor={textareaId}
          className="mb-6 font-sans text-s13 font-w500 tracking-givmo text-text-secondary"
        >
          {label}
        </label>
      )}
      <textarea
        ref={ref}
        id={textareaId}
        rows={rows}
        aria-invalid={error || undefined}
        aria-describedby={describedBy}
        className={cn(
          'w-full resize-y rounded-md border bg-surface p-12',
          'font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text placeholder:text-text-placeholder',
          'outline-none transition-[box-shadow,border-color]',
          // Control boundary — same floor and same pair as Field/Checkbox/SearchInput: neutral-500
          // is 4.68:1 on white / 4.47:1 on surface-sunken, hover darkens to neutral-600 (7.54:1).
          'border-neutral-500 hover:border-neutral-600',
          'focus-visible:border-brand-500 focus-visible:shadow-ring',
          'disabled:cursor-not-allowed disabled:bg-neutral-50 disabled:text-text-tertiary',
          error &&
            'border-danger-500 hover:border-danger-500 focus-visible:border-danger-500 focus-visible:shadow-ring-danger',
          className,
        )}
        {...testIdProp(testId)}
        {...props}
      />
      {hasErrorMessage ? (
        <p
          id={errorId}
          className="mt-6 font-sans text-s12 font-w400 tracking-givmo text-danger-600"
        >
          {errorMessage}
        </p>
      ) : helper != null ? (
        <p
          id={helperId}
          className="mt-6 font-sans text-s12 font-w400 tracking-givmo text-text-tertiary"
        >
          {helper}
        </p>
      ) : null}
    </div>
  );
});
